import { ExportState, SelectedExportOption } from './ExportState';
import { FiltersMap } from '../../model';
import {
  cairoCancelExportModalCtaClicked,
  cairoExportCtaClicked,
  cairoExportFailedModalCtaClicked,
  cairoExportModalCtaClicked,
  cairoExportProcessEnd,
  cairoExportProcessStart,
  cairoFileDownloadToastDismissed,
  cairoFileDownloadToastDisplayed,
  cairoFileDownloadToastDownloadButtonClicked,
  cairoItemToggledInExportModal,
  cairoUserAttemptsToCancelTheExport,
} from '@wix/bex-utils/@wix/bi-logger-os-data/v2';
import { withoutIrrelevant } from '../../util';
import { ReportProps } from '@wix/fe-essentials/bi';
import { action } from 'mobx';
import { v4 as uuid } from 'uuid';
import {
  ExportAsyncJob,
  ExportQueryV2 as QueryV2,
} from '@wix/bex-utils/@wix/ambassador-fedinfra-exportservice-v1-export-async-job/types';
import { getJobItemsAbsoluteCounter } from '../getJobItemsAbsoluteCounter';

const withoutDefaults = withoutIrrelevant<
  | 'currentTab'
  | 'currentView'
  | 'currentFilters'
  | 'currentSortOrder'
  | 'listSize'
  | 'filteredListSize'
  | 'initialItems'
  | 'availableExportTypes'
  | 'selectedExportType'
  | 'numItems'
>();

const exportTypeToBiExportType: Record<SelectedExportOption, string> = {
  all: 'All Items',
  filtered: 'Filtered Items',
  selected: 'Selected Items',
};

export class ExportStateBIReporter<T, F extends FiltersMap> {
  private readonly state;

  private readonly exportBiUniqueId = uuid();

  constructor(state: ExportState<T, F>) {
    this.state = state;
  }

  get reportBi() {
    return this.state.reportBi;
  }

  async _reportWithDefaultParams(reportProps: ReportProps) {
    const { state, exportBiUniqueId } = this;
    this.reportBi({
      ...reportProps,
      params: {
        ...reportProps.params,
        exportId: exportBiUniqueId,
        supportsTotal: state.collection.result.supportTotal,
        availableExportTypes: JSON.stringify({
          ...(state.disabledRadios.indexOf('all') === -1 && {
            [exportTypeToBiExportType.all]:
              await state.totalForExportGettersMap.all(),
          }),
          ...(state.disabledRadios.indexOf('filtered') === -1 && {
            [exportTypeToBiExportType.filtered]:
              state.totalForExportGettersMap.filtered(),
          }),
          ...(state.disabledRadios.indexOf('selected') === -1 && {
            [exportTypeToBiExportType.selected]:
              state.totalForExportGettersMap.selected(),
          }),
        }),
      },
    });
  }

  async _reportWithSelectedExportParams(reportProps: ReportProps) {
    const { state } = this;
    await this._reportWithDefaultParams({
      ...reportProps,
      params: {
        ...reportProps.params,
        selectedExportType:
          exportTypeToBiExportType[state.selectedExportOption],
        numItems: (await state.totalForExportGettersMap[
          state.selectedExportOption
        ]()) as number | undefined,
      },
    });
  }

  onExportTypeChange({
    prev,
    next,
  }: {
    prev: SelectedExportOption;
    next: SelectedExportOption;
  }) {
    this._reportWithDefaultParams(
      withoutDefaults(cairoItemToggledInExportModal)({
        newSelectedExportType: exportTypeToBiExportType[next],
        prevSelectedExportType: exportTypeToBiExportType[prev],
      }),
    );
  }

  clickOpenModal() {
    const { state } = this;

    this._reportWithDefaultParams(
      withoutDefaults(cairoExportCtaClicked)({
        defaultExportType: exportTypeToBiExportType[state.selectedExportOption],
        numSelectedItems: state.totalForExportGettersMap.selected() as
          | number
          | undefined,
      }),
    );
  }

  beforeToast() {
    const { state, reportBi } = this;

    reportBi(
      withoutDefaults(cairoFileDownloadToastDisplayed)({
        exportId: this.exportBiUniqueId,
        exportType: exportTypeToBiExportType[state.selectedExportOption],
        numSelectedItems: state.totalForExportGettersMap.selected() as
          | number
          | undefined,
        try: state.retries,
      }),
    );

    return {
      startTime: performance.now(),
    };
  }

  onToastActionClick({ startTime }: { startTime: number }) {
    const { state, reportBi } = this;

    reportBi(
      withoutDefaults(cairoFileDownloadToastDownloadButtonClicked)({
        exportId: this.exportBiUniqueId,
        exportType: exportTypeToBiExportType[state.selectedExportOption],
        numSelectedItems: state.totalForExportGettersMap.selected() as
          | number
          | undefined,
        duration: Math.round(performance.now() - startTime),
      }),
    );
  }

  onRetryExportToClick() {
    this._reportWithSelectedExportParams(
      withoutDefaults(cairoExportFailedModalCtaClicked)({
        ctaName: 'Try Again',
        try: this.state.retries,
      }),
    );
  }

  onFailedCancel() {
    this._reportWithSelectedExportParams(
      withoutDefaults(cairoExportFailedModalCtaClicked)({
        ctaName: 'Cancel',
        try: this.state.retries,
      }),
    );
  }

  onFailedClose() {
    this._reportWithSelectedExportParams(
      withoutDefaults(cairoExportFailedModalCtaClicked)({
        ctaName: 'X Button',
        try: this.state.retries,
      }),
    );
  }

  onExportToClick() {
    this._reportWithSelectedExportParams(
      withoutDefaults(cairoExportModalCtaClicked)({
        action: 'export',
        origin: 'Export Button',
      }),
    );
  }

  onExportStart() {
    const startTime = performance.now();

    this._reportWithSelectedExportParams(
      withoutDefaults(cairoExportProcessStart)({
        try: this.state.retries,
      }),
    );

    return action(
      async ({
        job,
        query,
        result,
        saveAs,
      }: {
        job: ExportAsyncJob | null;
        query: QueryV2;
        result: 'success' | 'fail' | 'stopped by user';
        saveAs?: string;
      }) => {
        const {
          state: { retries, totalForExport },
        } = this;

        this._reportWithSelectedExportParams(
          withoutDefaults(cairoExportProcessEnd)({
            duration: Math.round(performance.now() - startTime),
            try: retries,
            result,
            fileName: saveAs || '',
            numItemsExported:
              job != null
                ? getJobItemsAbsoluteCounter(job, { query, totalForExport })
                : 0,
          }),
        );
      },
    );
  }

  onCancelClick() {
    this._reportWithSelectedExportParams(
      withoutDefaults(cairoExportModalCtaClicked)({
        action: 'dismiss',
        origin: 'Cancel Button',
      }),
    );
  }

  onCancelExportButtonClick() {
    const { state } = this;
    const { retries, exportStartTime } = state;
    const duration = performance.now() - exportStartTime;
    this._reportWithSelectedExportParams(
      withoutDefaults(cairoCancelExportModalCtaClicked)({
        duration,
        try: retries,
        ctaName: 'Cancel Export',
        modalType: 'Export',
      }),
    );
  }

  onCancelExportGoBackButtonClick() {
    const { state } = this;
    const { retries, exportStartTime } = state;
    const duration = performance.now() - exportStartTime;
    this._reportWithSelectedExportParams(
      withoutDefaults(cairoCancelExportModalCtaClicked)({
        duration,
        try: retries,
        ctaName: 'Go Back',
        modalType: 'Export',
      }),
    );
  }

  onCancelExportCloseButtonClick() {
    const { state } = this;
    const { retries, exportStartTime } = state;
    const duration = performance.now() - exportStartTime;
    this._reportWithSelectedExportParams(
      withoutDefaults(cairoCancelExportModalCtaClicked)({
        duration,
        try: retries,
        ctaName: 'X Button',
        modalType: 'Export',
      }),
    );
  }

  onExportCloseAttemptClick() {
    const { state } = this;
    const { retries, exportStartTime } = state;
    const duration = performance.now() - exportStartTime;
    this._reportWithSelectedExportParams(
      withoutDefaults(cairoUserAttemptsToCancelTheExport)({
        duration,
        origin: 'X Button',
        try: retries,
      }),
    );
  }

  onExportCancelAttemptClick() {
    const { state } = this;
    const { retries, exportStartTime } = state;
    const duration = performance.now() - exportStartTime;
    this._reportWithSelectedExportParams(
      withoutDefaults(cairoUserAttemptsToCancelTheExport)({
        duration,
        origin: 'Cancel Button',
        try: retries,
      }),
    );
  }

  onCloseClick() {
    this._reportWithSelectedExportParams(
      withoutDefaults(cairoExportModalCtaClicked)({
        action: 'dismiss',
        origin: 'X Button',
      }),
    );
  }

  onToastDismissed({
    startTime,
    by: dismissMode,
  }: {
    startTime: number;
    by: 'manual' | 'automatic';
  }) {
    const { state, reportBi } = this;

    reportBi(
      withoutDefaults(cairoFileDownloadToastDismissed)({
        exportId: this.exportBiUniqueId,
        exportType: state.selectedExportOption,
        numSelectedItems: state.totalForExportGettersMap.selected() as
          | number
          | undefined,
        duration: Math.round(performance.now() - startTime),
        dismissMode,
      }),
    );
  }
}
